import { useEffect, useMemo, useRef, useState } from 'react';
import './ConfigBarcodes.scss';
import { useApiBarcodes } from '../../api/api.barcodes';
import Button from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react';
import DataGrid, { Column, Button as Butt, Editing, Item as Items, Paging, Toolbar, Popup, Lookup, RequiredRule, Selection, SearchPanel, Scrolling, Form } from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { useAppSelector } from '../../contexts/hooks';
import { useScreenSize } from '../../utils/media-query';
import { Item } from 'devextreme-react/form';

// Definir tipos para impresoras de códigos de barras
interface ImpresoraCodigoBarras {
  Cbp_Id?: number; // No se muestra en la UI
  Cbp_Sd_Id: number;
  Cbp_Nombre: string;
  Cbp_Descripcion: string;
  Cbp_Url: string;
  Cbp_Estado: boolean;
}

const ConfigBarcodesIndex = () => {
  const { isMedium, isSmall } = useScreenSize();
  const { getPrintsBarcodes, addPrintBarcode, editPrintBarcode, deletePrintBarcode } = useApiBarcodes();
  const [data, setData] = useState<ImpresoraCodigoBarras[]>([]);
  const dataGridRef = useRef<DataGrid>(null);
  const Sedes = useAppSelector((data) => data.configuration.Data?.ComboBox)?.find((x) => x.Name === 'SysI_Sedes')?.Data;

  useEffect(() => {
    getPrintsBarcodes().then((res) => {
      setData(res);
    });
  }, []);

  const customData = useMemo(() => {
    let cachedData: ImpresoraCodigoBarras[] = [];

    return new CustomStore({
      key: 'Cbp_Id',
      loadMode: 'processed',
      load() {
        return getPrintsBarcodes()
          .then((res) => {
            cachedData = res; // Guardar datos en cache
            return {
              data: res,
              totalCount: res.totalCount,
              summary: res.summary,
              groupCount: res.groupCount,
            };
          })
          .catch(() => {
            throw new Error('Error al cargar datos de impresoras');
          });
      },
      insert: async (values) => {
        return addPrintBarcode(values)
          .then((res) => {
            if (res === 105) {
              dataGridRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error insertando impresora');
          });
      },
      update: async (key, values) => {
        // Buscar el registro completo en el cache
        const fullRecord = cachedData.find((item) => item.Cbp_Id === key);
        // Fusionar datos completos con cambios
        const completeData = { ...fullRecord, ...values };

        return editPrintBarcode(completeData)
          .then((res) => {
            if (res === 105) {
              dataGridRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error actualizando impresora');
          });
      },
      remove: async (key) => {
        return deletePrintBarcode(key)
          .then((res) => {
            if (res === 105) {
              dataGridRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error eliminando impresora');
          });
      },
    });
  }, [data]);

  const customDataSrc = new DataSource({
    store: customData,
    reshapeOnPush: true,
  });

  // const handleSelection = useCallback(
  //   ({ selectedRowsData }: DataGridTypes.SelectionChangedEvent) => {
  //     const dataGrid = selectedRowsData[0];
  //     setSelectedRow(dataGrid);
  //   },
  //   [setSelectedRow]
  // );

  // const handleUpdateData = useCallback(() => {
  //   getCodigoBarrasImpresoras().then((res) => {
  //     setData(res);
  //   });
  // }, [setData]);

  // const estadoOptions = [
  //   { value: 'Activa', text: 'Activa' },
  //   { value: 'Inactiva', text: 'Inactiva' }
  // ];

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper view-wrapper-contact-list'>
        <DataGrid
          className='grid'
          dataSource={customDataSrc}
          height='100%'
          repaintChangesOnly={true}
          highlightChanges={true}
          rowAlternationEnabled={true}
          showColumnLines={true}
          //onSelectionChanged={}
        >
          <SearchPanel visible={true} />
          <Scrolling columnRenderingMode='standard' mode='virtual' />
          <Editing
            mode='popup'
            allowAdding
            allowDeleting
            allowUpdating
            texts={{
              saveRowChanges: 'Guardar',
              cancelRowChanges: 'Cancelar',
              deleteRow: 'Eliminar',
              addRow: 'Agregar',
            }}>
            <Popup title='CONFIGURACIÓN IMPRESORA DE CÓDIGOS DE BARRAS' width={900} height={'70%'} fullScreen={isMedium || isSmall} showTitle={true} />
            <Form>
              <Item dataField='Cbp_Nombre' />
              <Item dataField='Cbp_Descripcion' />
              <Item dataField='Cbp_Url' />
              <Item dataField='Cbp_Sd_Id' editorType='dxSelectBox' />
              <Item dataField='Cbp_Estado' editorType='dxCheckBox' />
            </Form>
          </Editing>
          <Paging enabled={false} />
          <Selection mode='single' />
          <Toolbar>
            <Items location='before'>
              <div className='grid-header'>Impresoras de Códigos de Barras</div>
            </Items>
            <Item name='searchPanel' />
            <Item name='addRowButton' />
            <Items location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button
                id='RefrescarImpresoras'
                icon='refresh'
                text='Actualizar'
                stylingMode='text'
                //onClick={handleUpdateData}
              />
              <Tooltip target={'#RefrescarImpresoras'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Refrescar datos
              </Tooltip>
            </Items>
          </Toolbar>

          <Column caption='ID' dataField='Cbp_Id' alignment='center' width={70} allowEditing={false} />
          <Column caption='SEDE' allowEditing={true} dataField='Cbp_Sd_Id' alignment='center' width={150}>
            <Lookup dataSource={Sedes} valueExpr='Sd_Id' displayExpr='Sd_Nombre' />
            <RequiredRule />
          </Column>
          <Column caption='NOMBRE' allowEditing={true} dataField='Cbp_Nombre' alignment='left'>
            <RequiredRule />
          </Column>

          <Column caption='DESCRIPCIÓN' allowEditing={true} dataField='Cbp_Descripcion' alignment='left'>
            <RequiredRule />
          </Column>

          <Column caption='URL' allowEditing={true} dataField='Cbp_Url' alignment='left' width={200}>
            <RequiredRule />
          </Column>

          <Column caption='ESTADO' allowEditing={true} dataField='Cbp_Estado' alignment='center' width={100}></Column>

          <Column type='buttons' caption={'ACCIÓN'} width={120}>
            <Butt name='edit' />
            <Butt name='delete' />
            <Butt hint='Probar' icon='print' visible={true} disabled={false} />
          </Column>
        </DataGrid>
      </div>
    </div>
  );
};

export default ConfigBarcodesIndex;
